import React, { useState, useEffect } from 'react'
import { getGeneralInquiry } from 'lib/services/inquiry'
import {
  getActiveMainFilters,
  getActiveAdditionalFilters,
  saveAdditionalFilters,
  mapFiltersToGroups,
} from 'lib/services/filters'
import { getActivePagination, savePagination } from 'lib/services/pagination'
import { scrollTo } from 'lib/utils/scrolls'
import eventService from 'lib/services/pixels-headless/events'
import PrimaryFilters from 'components/EventIndex/Filters'
import EventCount from 'components/EventIndex/Count'
import EventList from 'components/EventIndex/List'
import Suggestions from 'components/EventIndex/Suggestions'
import Pagination from 'components/EventIndex/Pagination'
import LoadingSpinner from 'components/LoadingSpinner'
import { useRouter } from 'next/router'
import FilterTypes from './FilterTypes'
import FilterModal from './FilterModal'
import OrderByDropdown from './FiltersSecondary/OrderByDropdown'
import styles from './EventIndex.module.scss'
import Map from './Map'
import { mapSelectionsToQuery } from '../../lib/utils/filters'

const serviceOrder = [
  'Tilat',
  'Elämykset ja aktiviteetit',
  'Catering ja tarjoilut',
  'Esiintyjät',
  'Muut palvelut',
]

export default function EventIndex({ page }) {
  const filters = page.fields.EventFilters
  const [isReady, setIsReady] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [query, setQuery] = useState([])
  const [mainFilters, setMainFilters] = useState(getActiveMainFilters(filters))
  const [secondaryFilters, setSecondaryFilters] = useState([])
  const [orderBy, setOrderBy] = useState('rand')
  const [currentPage, setCurrentPage] = useState(1)
  const [events, setEvents] = useState([])
  const [totalEvents, setTotalEvents] = useState(0)
  const [pages, setPages] = useState(0)
  const [existingActiveFilters, setExistingActiveFilters] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [activeIndex, setActiveIndex] = useState()
  const [largeButtonText, setLargeButtonText] = useState('') // The buttons with icons
  const [orderedTypes, setOrderedTypes] = useState([])
  const isLastPage = currentPage === pages || pages === 0
  const [mapIsToggled, setMapIsToggled] = useState(false)
  const [mapCenterLocation, setMapCenterLocation] = useState(null)
  const [mapZoom, setMapZoom] = useState(null)
  const [supportsWebGL, setSupportsWebGL] = useState(false)

  const router = useRouter()

  /**
   * Check if browser supports WebGL
   * @return {boolean}
   */
  const checkWebGLSupport = () => {
    try {
      const canvas = document.createElement('canvas')
      return !!(
        window.WebGLRenderingContext &&
        (canvas.getContext('webgl') || canvas.getContext('experimental-webgl'))
      )
    } catch (e) {
      return false
    }
  }

  const toggleModalVisibility = () => {
    setShowModal(!showModal)
  }

  const setPagination = (newPage, scrollToTop = true) => {
    setCurrentPage(newPage)
    savePagination(newPage)

    if (scrollToTop) {
      scrollTo('#scroll-anchor')
    }
  }

  /**
   * Get event results from API.
   */
  const getEvents = async (thisPage = currentPage) => {
    setIsLoading(true)
    const result = await eventService.getResults(query, thisPage, orderBy)
    setEvents(result.events)
    setTotalEvents(result.total)
    setPages(result.pages)
    setIsLoading(false)
    setIsReady(true)
  }

  /**
   * Gets latitutde and longitude from location slug and api response
   * @param location
   * @return {{lng: null, zoom: null, lat: null}}
   */
  const getLocationLatLng = (location) => {
    const currentLocationApiResponse = filters.locations.find(
      (e) => e.slug === location,
    )

    const { lat, lng, zoom } = currentLocationApiResponse || {
      lat: null,
      lng: null,
      zoom: null,
    }

    return { lat, lng, zoom }
  }

  /**
   * Handle query from main filters.
   * Combine with existing secondary filters
   */
  const handleMainSelections = (data) => {
    setMainFilters(data)

    if (!mapIsToggled) {
      const newQuery = [...data, ...secondaryFilters]
      setQuery(newQuery)
    } else {
      const selectedLocation = data.find((e) => e.filter === 'event_location')
        ?.values[0]

      const { lat, lng, zoom } = getLocationLatLng(selectedLocation)

      if (lat && lng && zoom) {
        setMapCenterLocation({ lat: Number(lat), lng: Number(lng) })
        setMapZoom(Number(zoom))
      }

      // Remove location from query if map is not toggled
      const dataWithoutLocation = data.filter(
        (e) => e.filter !== 'event_location',
      )
      const newQuery = [...dataWithoutLocation, ...secondaryFilters]
      setQuery(newQuery)
    }

    setPagination(1, false)
  }

  /**
   * Handle change in filter type selections (the large buttons which specify a broad category).
   * @param data
   */

  /**
   * Handle query from secondary filters.
   * Combine with existing primary filters
   */
  const handleSecondarySelections = (
    data,
    resetPagination = false,
    activeOrderBy = 'name',
  ) => {
    // Store selections in localstorage.
    saveAdditionalFilters(data)

    /**
     * Also update to existingActives
     * There may be multiple instances of 2ndary filters
     * component in page, and they need to reset their state too
     */
    setExistingActiveFilters(mapFiltersToGroups(data))

    setSecondaryFilters(data)
    const newQuery = [...data, ...mainFilters]
    setQuery(newQuery)
    setOrderBy(activeOrderBy)

    if (resetPagination) {
      setPagination(1, false)
    }
  }

  const handleServicePackageCallback = (data, largeButtonString, index) => {
    setActiveIndex(index)
    // Store selections in localstorage.
    saveAdditionalFilters(data)

    /**
     * Also update to existingActives
     * There may be multiple instances of 2ndary filters
     * component in page, and they need to reset their state too
     */
    setExistingActiveFilters(mapFiltersToGroups(data))

    setSecondaryFilters(data)
    const newQuery = [...data, ...mainFilters]
    setQuery(newQuery)
    /* setOrderBy(activeOrderBy)

    if (resetPagination) {
      setPagination(1, false)
    } */
    setLargeButtonText(largeButtonString)
  }

  /**
   * Handle query from changing to map.
   * In this case we don't need pagination
   */
  const handleOpenMap = () => {
    setMapIsToggled(!mapIsToggled)
    if (!mapIsToggled) {
      getEvents(0)
      // Push current page state and URL to history stack
      router.push('/tapahtumat?view=map', undefined, { shallow: true })
    } else {
      getEvents()
      // When closing the map view
      router.push('/tapahtumat?view=list', undefined, { shallow: true })
    }
  }

  /**
   * Load existing active filters & paged value from store.
   * Do it only once on load.
   */

  // eslint-disable-next-line no-shadow
  const getInitialActiveType = (query) => {
    // Not working yet with greenish filters
    if (query.length === 0) {
      return
    }
    const { filter } = query[0]
    if (filter !== 'event_type') {
      return
    }
    const index = orderedTypes.map((i) => i.slug).indexOf(query[0].values[0])
    setActiveIndex(index)
  }

  useEffect(() => {
    const viewMode = new URLSearchParams(window.location.search).get('view')
    if (viewMode === 'map') {
      setMapIsToggled(true)
    }

    // Check WebGL support
    const webGLSupport = checkWebGLSupport()
    setSupportsWebGL(webGLSupport)
    if (!webGLSupport) {
      setMapIsToggled(false)
    }
  }, [])

  useEffect(() => {
    if (isReady) {
      const mainFiltersFromStore = getActiveMainFilters(filters)
      const secondaryFiltersFromStore = getActiveAdditionalFilters()
      const paginationFromStore = getActivePagination()
      setMainFilters(mainFiltersFromStore)
      setSecondaryFilters(secondaryFiltersFromStore.query)
      setExistingActiveFilters(secondaryFiltersFromStore)
      setCurrentPage(paginationFromStore)
      getInitialActiveType(secondaryFiltersFromStore.query)

      if (!mapIsToggled) {
        // When list is shown, combine main and secondary filters
        const newQuery = [
          ...mainFiltersFromStore,
          ...secondaryFiltersFromStore.query,
        ]
        setQuery(newQuery)
        setOrderBy(orderBy)
      } else {
        // When map is shown, remove location from query and handle zoom
        const selectedLocation = mainFiltersFromStore.find(
          (e) => e.filter === 'event_location',
        )?.values[0]

        const { lat, lng, zoom } = getLocationLatLng(selectedLocation)

        if (lat && lng && zoom) {
          setMapCenterLocation({ lat: Number(lat), lng: Number(lng) })
          setMapZoom(Number(zoom))
        }

        // Remove location from query if map is not toggled
        const dataWithoutLocation = mainFiltersFromStore.filter(
          (e) => e.filter !== 'event_location',
        )
        const newQuery = [
          ...dataWithoutLocation,
          ...secondaryFiltersFromStore.query,
        ]
        setQuery(newQuery)
        setOrderBy(orderBy)
      }
    }
  }, [isReady, mapIsToggled])

  /**
   * Fetch events whenever query changes
   */
  useEffect(async () => {
    // If empty query, use defaults from store.
    if (query.length === 0) {
      const inquiry = getGeneralInquiry()
      const queryFromStore = mapSelectionsToQuery(inquiry, filters)
      setQuery(queryFromStore)
    } else if (!mapIsToggled) {
      getEvents()
    } else {
      getEvents(0)
    }
  }, [query, currentPage, orderBy])

  useEffect(() => {
    if (!document) return
    const body = document.getElementById('body')
    if (showModal) {
      body.style.overflow = 'hidden'
    } else {
      body.style.overflowY = 'scroll'
    }
  }, [showModal])

  useEffect(() => {
    // Modify the types array to match the order of the serviceOrder array
    const newTypes = []
    serviceOrder.forEach((service) => {
      const type = filters.types.find((el) => el.name === service)
      if (type) {
        newTypes.push(type)
      }
    })
    setOrderedTypes(newTypes)
  }, [filters.types])

  const toggleOrderBy = (newOrderBy) => {
    if (newOrderBy !== orderBy) {
      setOrderBy(newOrderBy)
    }
  }

  return (
    <>
      {supportsWebGL && (
        <div
          className={`btn btn--primary ${styles.mapButton}`}
          onClick={handleOpenMap}
        >
          {!mapIsToggled ? (
            <>
              <div
                style={{
                  width: '30px',
                  height: '30px',
                  marginRight: '7px',
                  backgroundImage: `url(img/map.svg)`,
                  backgroundSize: '30px',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'center',
                  color: '#fff',
                }}
              />
              Avaa kartta
            </>
          ) : (
            <>
              <div
                style={{
                  width: '30px',
                  height: '30px',
                  marginRight: '7px',
                  backgroundImage: `url(img/grid_view.svg)`,
                  backgroundSize: '30px',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'center',
                  color: '#fff',
                }}
              />
              Avaa lista
            </>
          )}
        </div>
      )}
      <FilterModal
        toggleVisibility={toggleModalVisibility}
        visible={showModal}
        types={orderedTypes}
        features={filters.features}
        actives={existingActiveFilters}
        orderBy={orderBy}
        callback={handleSecondarySelections}
        count={totalEvents}
        isLoading={isLoading}
        largeButtonText={largeButtonText}
        typeCallback={handleServicePackageCallback}
        activeIndex={activeIndex}
        setCurrentPage={setCurrentPage}
      />
      <PrimaryFilters filters={filters} callback={handleMainSelections} />
      <FilterTypes
        actives={existingActiveFilters}
        types={orderedTypes}
        callback={handleServicePackageCallback}
        activeIndex={activeIndex}
        setCurrentPage={setCurrentPage}
      />
      <EventCount
        count={totalEvents}
        isLoading={isLoading}
        toggleVisibility={toggleModalVisibility}
      />

      {(!mapIsToggled || !supportsWebGL) && (
        <OrderByDropdown
          activeOrderBy={orderBy}
          toggleOrderBy={toggleOrderBy}
        />
      )}
      <div id="scroll-anchor" />
      {isLoading && <LoadingSpinner />}
      {mapIsToggled && supportsWebGL ? (
        <>
          {!isLoading && (
            <Map events={events} center={mapCenterLocation} zoom={mapZoom} />
          )}
        </>
      ) : (
        <>
          {!isLoading && <EventList events={events} linkParam={'haku'} />}
          {!isLoading && isLastPage && <Suggestions callback={setShowModal} />}
          {!isLoading && pages > 1 && (
            <Pagination
              pages={pages}
              changePage={setPagination}
              active={currentPage}
            />
          )}
        </>
      )}
    </>
  )
}
